import { AccountError, type CommerceSource } from '@akademi/site-builder-runtime';

/**
 * The shop's installers (a kitchen shop calls them fitters): applying to join, the documents page
 * an installer reaches from a signed link in their email, and a signed-in installer's own areas,
 * jobs and quotes.
 *
 * <p>Akademi's `CommerceSource` has none of this. A host that offers it adds the method groups
 * below, and a block reads them as optional, as the trade application's are read: a host without
 * them shows the block's placeholder.
 */

/** The trades every shop on the platform shares, in the backend's order (`InstallerTrade`). */
export const INSTALLER_TRADES = [
  'KITCHEN_FITTING',
  'JOINERY',
  'ELECTRICAL',
  'PLUMBING',
  'GAS',
  'TILING',
  'PLASTERING',
  'SOLAR_PV',
  'BATTERY_STORAGE',
  'EV_CHARGERS',
  'OTHER',
] as const;
export type InstallerTrade = (typeof INSTALLER_TRADES)[number];

/** The insurance an installer holds, one certificate each (`InstallerPolicyType`). */
export const INSTALLER_POLICY_TYPES = [
  'PUBLIC_LIABILITY',
  'EMPLOYERS_LIABILITY',
  'PROFESSIONAL_INDEMNITY',
  'CONTRACT_WORKS',
] as const;
export type InstallerPolicyType = (typeof INSTALLER_POLICY_TYPES)[number];

/** Public liability always; employer's liability too for a business that employs people. */
export function policyRequired(type: InstallerPolicyType, employsStaff: boolean): boolean {
  return type === 'PUBLIC_LIABILITY' || (type === 'EMPLOYERS_LIABILITY' && employsStaff);
}

/** The published agreement as a signer sees it, with the words beside the box they tick. */
export interface InstallerAgreementToSign {
  version: number;
  title: string;
  body: string;
  textHash: string;
  statement: string;
}

/** What the application form needs before it is filled. */
export interface InstallerRequirements {
  /** Null while the shop has not published one, when the form cannot be sent. */
  agreement: InstallerAgreementToSign | null;
  /** The shop's minimum cover per policy type, as decimal strings; only the types that have one. */
  minimumCover: Partial<Record<InstallerPolicyType, string>>;
}

/** A signature: the version and hash the page was given, the full name typed, the ticked box. */
export interface InstallerSigning {
  agreementVersion: number;
  textHash: string;
  fullName: string;
  agreed: boolean;
}

/** One policy as typed: `coverAmount` a decimal, `expiresOn` an ISO day. */
export interface InstallerPolicyInput {
  type: InstallerPolicyType;
  insurer: string;
  policyNumber: string;
  coverAmount: string;
  expiresOn: string;
}

export interface InstallerRegistrations {
  gasSafe?: string;
  niceic?: string;
  napit?: string;
  mcs?: string;
  otherName?: string;
  otherNumber?: string;
}

/** One application, as the host sends it. */
export interface InstallerApplicationRequest {
  businessName: string;
  contactName: string;
  /** Required from a guest; a signed-in shopper's application goes under their account's address. */
  email?: string;
  phone: string;
  companyNumber?: string;
  vatNumber?: string;
  businessAddress?: string;
  website?: string;
  trades: InstallerTrade[];
  otherTrade?: string;
  /** The postcode districts and areas as typed, which the backend reads as it reads them here. */
  areas: string;
  employsStaff: boolean;
  registrations?: InstallerRegistrations;
  policies: InstallerPolicyInput[];
  message?: string;
  language?: 'en' | 'tr';
  agreement: InstallerSigning;
}

/** One certificate file, and the policy it proves. */
export interface InstallerCertificateUpload {
  type: InstallerPolicyType;
  file: File;
}

/** What an applicant is told once their application is in. */
export interface InstallerApplicationStatus {
  status: string;
  businessName: string;
  email: string;
  submittedAt: string;
  /** Policy types still without a certificate: an application made with the sign-up (contract §7). */
  certificatesOutstanding?: InstallerPolicyType[];
}

/** One policy on the documents page. */
export interface InstallerSelfPolicy {
  id: number;
  type: InstallerPolicyType;
  /** `APPROVED`, `PENDING` (a renewal waiting for staff) or `REJECTED`. */
  status: string;
  insurer: string;
  policyNumber: string;
  coverAmount: string;
  /** ISO day. */
  expiresOn: string;
  required: boolean;
  expired: boolean;
  renewal: boolean;
  rejectionReason: string | null;
}

/** Where an installer stands, as their documents page shows it. */
export interface InstallerSelfView {
  businessName: string;
  contactName: string;
  email: string;
  /** `ACTIVE` or `SUSPENDED`. */
  status: string;
  agreementOutOfDate: boolean;
  signedVersion: number | null;
  /** The agreement to sign, only while theirs is out of date. */
  agreement: InstallerAgreementToSign | null;
  employsStaff: boolean;
  policies: InstallerSelfPolicy[];
  minimumCover: Partial<Record<InstallerPolicyType, string>>;
}

export type InstallerState = 'APPLIED' | 'ACTIVE' | 'SUSPENDED' | 'REJECTED';

/** Why an installer cannot quote just now. */
export type InstallerBlocker = 'AGREEMENT_OUT_OF_DATE' | 'NOT_INSURED' | 'SUSPENDED';

/** The signed-in shopper as an installer. */
export interface InstallerMe {
  state: InstallerState;
  businessName: string;
  trades: InstallerTrade[];
  /** Postcode districts (`LS6`) and whole areas (`LS`). */
  areas: string[];
  canQuote: boolean;
  blockers: InstallerBlocker[];
  /** A fresh `/installer-documents?token=` path to the documents page, or null. */
  documentsLink: string | null;
  /** `APPLIED` only: policy types whose certificate has not been uploaded yet; otherwise empty. */
  certificatesOutstanding?: InstallerPolicyType[];
}

export type InstallerQuoteStatus = 'SENT' | 'WITHDRAWN' | 'ACCEPTED' | 'DECLINED';

export interface InstallerJobQuote {
  id: number;
  /** A decimal string. */
  price: string;
  /** ISO day. */
  availableFrom: string;
  note: string | null;
  status: InstallerQuoteStatus;
  sentAt: string;
}

export type InstallerJobStatus = 'OPEN' | 'AWARDED' | 'CLOSED' | 'CANCELLED';

/** A job as an installer sees it: the district only, never the site or the customer, until an award. */
export interface InstallerJob {
  id: number;
  reference: string;
  title: string;
  /** The outward code, `LS6`. */
  district: string;
  /** Always null from the backend: the storefront names areas itself. */
  areaName: string | null;
  trades: InstallerTrade[];
  scope: string[];
  summary: string;
  readyFrom: string;
  budgetGuide: string | null;
  closesAt: string | null;
  packNotes: string | null;
  postedAt: string;
  status: InstallerJobStatus;
  /** How many quotes are in (sent or accepted); nobody else's details. */
  quotes: number;
  /** The full site postcode, only once this installer's quote was accepted. */
  sitePostcode: string | null;
  myQuote: InstallerJobQuote | null;
}

/** A quote as sent: `price` a decimal string, `availableFrom` an ISO day today or later. */
export interface InstallerQuoteRequest {
  price: string;
  availableFrom: string;
  note?: string;
}

/** What a host adds to its `CommerceSource` for installer applications. */
export interface InstallerApplicationMethods {
  /** The agreement to sign and the shop's minimum cover. `NOT_FOUND` on a shop that takes no applications. */
  installerApplicationRequirements(): Promise<InstallerRequirements>;
  /** A multipart form: the application, and a certificate per policy. */
  submitInstallerApplication(
    input: InstallerApplicationRequest,
    certificates: InstallerCertificateUpload[],
  ): Promise<InstallerApplicationStatus>;
  /**
   * The certificates the signed-in shopper's pending application still lacks — one made with the
   * sign-up, which cannot carry files. Answers `certificatesOutstanding` as it now stands.
   */
  uploadInstallerCertificates?(certificates: InstallerCertificateUpload[]): Promise<InstallerApplicationStatus>;
}

/** What a host adds for the documents page. `token` is the link's, sent as a header, never in a URL. */
export interface InstallerSelfMethods {
  installerSelf(token: string): Promise<InstallerSelfView>;
  signInstallerAgreement(token: string, signing: InstallerSigning): Promise<InstallerSelfView>;
  renewInstallerPolicy(token: string, policy: InstallerPolicyInput, certificate: File): Promise<InstallerSelfView>;
}

/**
 * What a host adds for a signed-in installer. Refusals are `AccountError`s: `NOT_SIGNED_IN`,
 * `NOT_INSTALLER` (signed in, neither an installer nor an applicant), `INVALID_INPUT` with the
 * backend's sentence, `CONFLICT` (a job no longer open), `NOT_FOUND`.
 */
export interface InstallerMeMethods {
  /** Where the shopper stands, from their profile; `NOT_INSTALLER` when neither installer nor applicant. */
  installerMe(): Promise<InstallerMe>;
  /** Saved with the profile; answers the standing as stored. */
  saveInstallerAreas(areas: string[]): Promise<InstallerMe>;
  installerJobs(): Promise<InstallerJob[]>;
  quoteInstallerJob(jobId: number, quote: InstallerQuoteRequest): Promise<InstallerJob>;
  withdrawInstallerQuote(jobId: number): Promise<InstallerJob>;
}

export type InstallerCommerceSource = CommerceSource &
  Partial<InstallerApplicationMethods> &
  Partial<InstallerSelfMethods> &
  Partial<InstallerMeMethods>;

// ---- areas ------------------------------------------------------------------------------

/** A UK postcode area (`LS`) or district (`LS1`, `SW1A`, `EC1A`, `W1`), as `InstallerInput.DISTRICT`. */
const DISTRICT = /^[A-Z]{1,2}(?:[0-9][A-Z0-9]?)?$/;
const SEPARATORS = /[\s,;/]+/;
const CONNECTORS = new Set(['AND', '&', '+']);
const RANGE_WORDS = new Set(['TO', 'TIL', 'THRU', 'THROUGH', 'UNTIL']);
export const MAX_INSTALLER_AREAS = 300;

export type AreasReading =
  | { ok: true; areas: string[] }
  | { ok: false; kind: 'EMPTY' }
  | { ok: false; kind: 'TOO_MANY'; max: number }
  | { ok: false; kind: 'NOT_DISTRICTS'; wrong: string[]; range: boolean };

/**
 * The areas typed, read exactly as the backend reads them (`InstallerInput.areas`): split on
 * spaces, commas, semicolons and slashes, upper-cased, once each in first-seen order; `and`, `&`
 * and `+` dropped; a range word (`LS1 to LS5`) refused rather than quietly losing the districts
 * between, and anything else that is not a district named back.
 */
export function readInstallerAreas(text: string): AreasReading {
  const areas: string[] = [];
  const wrong: string[] = [];
  let range = false;
  for (const token of text.trim().split(SEPARATORS)) {
    const typed = token.trim();
    const district = typed.toUpperCase();
    if (!district || CONNECTORS.has(district)) continue;
    if (RANGE_WORDS.has(district)) {
      range = true;
      if (!wrong.includes(typed)) wrong.push(typed);
    } else if (DISTRICT.test(district)) {
      if (!areas.includes(district)) areas.push(district);
    } else if (!wrong.includes(typed)) {
      wrong.push(typed);
    }
  }
  if (wrong.length) return { ok: false, kind: 'NOT_DISTRICTS', wrong, range };
  if (!areas.length) return { ok: false, kind: 'EMPTY' };
  if (areas.length > MAX_INSTALLER_AREAS) return { ok: false, kind: 'TOO_MANY', max: MAX_INSTALLER_AREAS };
  return { ok: true, areas };
}

// ---- the form ---------------------------------------------------------------------------

/** The backend's limits (`InstallerInput`), which the form's fields hold to. */
export const INSTALLER_FIELD_LIMITS = {
  businessName: 300,
  contactName: 200,
  email: 200,
  phone: 40,
  number: 40,
  businessAddress: 500,
  website: 300,
  message: 2000,
  otherTrade: 200,
  schemeName: 100,
  insurer: 200,
  policyNumber: 100,
  typedName: 200,
  quoteNote: 500,
} as const;

/** Each certificate at most this large: `CertificateUpload.MAX_BYTES`. */
export const MAX_CERTIFICATE_BYTES = 26_214_400;
export const CERTIFICATE_ACCEPT = '.pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png';

/** One policy as typed, with its file. */
export interface InstallerPolicyDraft {
  insurer: string;
  policyNumber: string;
  coverAmount: string;
  expiresOn: string;
  file: File | null;
}

export const EMPTY_POLICY_DRAFT: InstallerPolicyDraft = {
  insurer: '',
  policyNumber: '',
  coverAmount: '',
  expiresOn: '',
  file: null,
};

/** The form as typed. */
export interface InstallerApplicationDraft {
  businessName: string;
  contactName: string;
  email: string;
  phone: string;
  companyNumber: string;
  vatNumber: string;
  businessAddress: string;
  website: string;
  trades: InstallerTrade[];
  otherTrade: string;
  areas: string;
  employsStaff: boolean;
  gasSafe: string;
  niceic: string;
  napit: string;
  mcs: string;
  otherName: string;
  otherNumber: string;
  message: string;
  /** Policies the applicant is giving, by type; a required one is always here. */
  policies: Partial<Record<InstallerPolicyType, InstallerPolicyDraft>>;
  fullName: string;
  agreed: boolean;
}

export const EMPTY_INSTALLER_DRAFT: InstallerApplicationDraft = {
  businessName: '',
  contactName: '',
  email: '',
  phone: '',
  companyNumber: '',
  vatNumber: '',
  businessAddress: '',
  website: '',
  trades: [],
  otherTrade: '',
  areas: '',
  employsStaff: false,
  gasSafe: '',
  niceic: '',
  napit: '',
  mcs: '',
  otherName: '',
  otherNumber: '',
  message: '',
  policies: { PUBLIC_LIABILITY: { ...EMPTY_POLICY_DRAFT } },
  fullName: '',
  agreed: false,
};

export type InstallerDraftField =
  | 'businessName'
  | 'contactName'
  | 'phone'
  | 'trades'
  | 'otherTrade'
  | 'areas'
  | 'fullName'
  | 'agreed';

/** Why the form cannot be sent yet. */
export type InstallerDraftProblem =
  | { kind: 'MISSING'; fields: InstallerDraftField[] }
  | { kind: 'AREAS'; reading: Exclude<AreasReading, { ok: true }> }
  | { kind: 'OTHER_REGISTRATION' }
  | { kind: 'POLICY_MISSING'; type: InstallerPolicyType }
  | { kind: 'POLICY_INCOMPLETE'; type: InstallerPolicyType }
  | { kind: 'COVER_NOT_A_NUMBER'; type: InstallerPolicyType }
  | { kind: 'COVER_BELOW_MINIMUM'; type: InstallerPolicyType; minimum: string }
  | { kind: 'POLICY_EXPIRED'; type: InstallerPolicyType }
  | { kind: 'CERTIFICATE_MISSING'; type: InstallerPolicyType }
  | { kind: 'CERTIFICATE_TOO_LARGE'; type: InstallerPolicyType }
  | { kind: 'NO_AGREEMENT' };

/** A cover amount as the backend reads one: `£5,000,000` is 5000000. Null when not a positive number. */
export function coverNumber(value: string): number | null {
  const cleaned = value.replace(/[£$€,_\s]/g, '');
  if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) return null;
  const amount = Number(cleaned);
  return amount > 0 ? amount : null;
}

/** Today as an ISO day in the shopper's own calendar. */
export function isoToday(now = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/** Checks one policy draft as the backend will: every field, a real cover, not expired, a file within the limit. */
export function policyProblem(
  type: InstallerPolicyType,
  draft: InstallerPolicyDraft,
  options: { minimum?: string; today: string; needsFile?: boolean },
): InstallerDraftProblem | null {
  if (!draft.insurer.trim() || !draft.policyNumber.trim() || !draft.coverAmount.trim() || !draft.expiresOn) {
    return { kind: 'POLICY_INCOMPLETE', type };
  }
  const cover = coverNumber(draft.coverAmount);
  if (cover === null) return { kind: 'COVER_NOT_A_NUMBER', type };
  const minimum = options.minimum ? Number(options.minimum) : 0;
  if (minimum > 0 && cover < minimum) return { kind: 'COVER_BELOW_MINIMUM', type, minimum: options.minimum! };
  if (draft.expiresOn < options.today) return { kind: 'POLICY_EXPIRED', type };
  if (options.needsFile !== false && !draft.file) return { kind: 'CERTIFICATE_MISSING', type };
  if (draft.file && draft.file.size > MAX_CERTIFICATE_BYTES) return { kind: 'CERTIFICATE_TOO_LARGE', type };
  return null;
}

/**
 * What is missing or wrong, checked here so the applicant hears it before the round trip; the
 * backend checks it all again. A shop below its own minimum cover is said here too, because the
 * shop cannot approve it, and nobody should upload a certificate to be refused a week later.
 */
export function installerDraftProblem(
  draft: InstallerApplicationDraft,
  options: {
    requirements: InstallerRequirements;
    today?: string;
    /** The application goes with a sign-up, which cannot carry files: certificates follow it. */
    certificatesLater?: boolean;
  },
): InstallerDraftProblem | null {
  const missing: InstallerDraftField[] = [];
  if (!draft.businessName.trim()) missing.push('businessName');
  if (!draft.contactName.trim()) missing.push('contactName');
  if (!draft.phone.trim()) missing.push('phone');
  if (!draft.trades.length) missing.push('trades');
  if (draft.trades.includes('OTHER') && !draft.otherTrade.trim()) missing.push('otherTrade');
  if (!draft.areas.trim()) missing.push('areas');
  if (!draft.fullName.trim()) missing.push('fullName');
  if (!draft.agreed) missing.push('agreed');
  if (missing.length) return { kind: 'MISSING', fields: missing };

  const reading = readInstallerAreas(draft.areas);
  if (!reading.ok) return { kind: 'AREAS', reading };
  if (!draft.otherName.trim() !== !draft.otherNumber.trim()) return { kind: 'OTHER_REGISTRATION' };

  const today = options.today ?? isoToday();
  for (const type of INSTALLER_POLICY_TYPES) {
    const policy = draft.policies[type];
    if (!policy) {
      if (policyRequired(type, draft.employsStaff)) return { kind: 'POLICY_MISSING', type };
      continue;
    }
    const problem = policyProblem(type, policy, {
      minimum: options.requirements.minimumCover[type],
      today,
      needsFile: !options.certificatesLater,
    });
    if (problem) return problem;
  }
  if (!options.requirements.agreement) return { kind: 'NO_AGREEMENT' };
  return null;
}

/** The application to send, and its certificates: every field trimmed, blank optional ones left out. */
export function installerApplicationRequest(
  draft: InstallerApplicationDraft,
  options: { agreement: InstallerAgreementToSign; accountEmail?: string | null; language?: string | null },
): { request: InstallerApplicationRequest; certificates: InstallerCertificateUpload[] } {
  const email = (options.accountEmail ?? draft.email).trim().toLowerCase();
  const reading = readInstallerAreas(draft.areas);
  const request: InstallerApplicationRequest = {
    businessName: draft.businessName.trim(),
    contactName: draft.contactName.trim(),
    ...(email ? { email } : {}),
    phone: draft.phone.trim(),
    trades: INSTALLER_TRADES.filter((trade) => draft.trades.includes(trade)),
    // The areas go as the canonical list, so what the applicant saw checked is what is stored.
    areas: reading.ok ? reading.areas.join(', ') : draft.areas.trim(),
    employsStaff: draft.employsStaff,
    policies: [],
    agreement: {
      agreementVersion: options.agreement.version,
      textHash: options.agreement.textHash,
      fullName: draft.fullName.trim(),
      agreed: draft.agreed,
    },
  };
  for (const name of ['companyNumber', 'vatNumber', 'businessAddress', 'website', 'message'] as const) {
    const value = draft[name].trim();
    if (value) request[name] = value;
  }
  if (draft.trades.includes('OTHER') && draft.otherTrade.trim()) request.otherTrade = draft.otherTrade.trim();
  const registrations: InstallerRegistrations = {};
  for (const name of ['gasSafe', 'niceic', 'napit', 'mcs', 'otherName', 'otherNumber'] as const) {
    const value = draft[name].trim();
    if (value) registrations[name] = value;
  }
  if (Object.keys(registrations).length) request.registrations = registrations;
  const certificates: InstallerCertificateUpload[] = [];
  for (const type of INSTALLER_POLICY_TYPES) {
    const policy = draft.policies[type];
    if (!policy) continue;
    request.policies.push({
      type,
      insurer: policy.insurer.trim(),
      policyNumber: policy.policyNumber.trim(),
      coverAmount: policy.coverAmount.trim(),
      expiresOn: policy.expiresOn,
    });
    if (policy.file) certificates.push({ type, file: policy.file });
  }
  const language = (options.language ?? '').slice(0, 2).toLowerCase();
  if (language === 'en' || language === 'tr') request.language = language;
  return { request, certificates };
}

/** The refusals the installer blocks have their own words for; anything else reads as `UNAVAILABLE`. */
export const INSTALLER_ERROR_KINDS = [
  'APPLICATION_OPEN',
  'ALREADY_INSTALLER',
  'NO_AGREEMENT',
  'AGREEMENT_CHANGED',
  'INVALID_INPUT',
  'INVALID_FILE',
  'INVALID_LINK',
  'NOT_INSTALLER',
  'CONFLICT',
  'RATE_LIMITED',
  'NOT_SIGNED_IN',
  'NOT_FOUND',
  'UNAVAILABLE',
] as const;
export type InstallerErrorKind = (typeof INSTALLER_ERROR_KINDS)[number];

export function installerErrorKind(error: unknown): InstallerErrorKind {
  const kind: string = error instanceof AccountError ? error.kind : 'UNAVAILABLE';
  return (INSTALLER_ERROR_KINDS as readonly string[]).includes(kind) ? (kind as InstallerErrorKind) : 'UNAVAILABLE';
}

/** The backend's own sentence for a refusal it wrote for the reader, when there is one. */
export function refusalMessage(error: unknown): string | null {
  if (!(error instanceof AccountError)) return null;
  const message = error.message;
  return message && message !== error.kind && !message.startsWith('storefront request failed') ? message : null;
}

/** A decimal amount as money, whole pounds when it has no pence: `5000000.00` is £5,000,000. */
export function formatCover(amount: string, language: string | undefined, currency = 'GBP'): string {
  const value = Number(amount);
  if (!Number.isFinite(value)) return amount;
  try {
    return new Intl.NumberFormat(language || 'en-GB', {
      style: 'currency',
      currency,
      maximumFractionDigits: Number.isInteger(value) ? 0 : 2,
      minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
    }).format(value);
  } catch {
    return amount;
  }
}
